import { useMemo, useState } from "react"
import { Link, router } from "@inertiajs/react"
import { FiArrowRight, FiCheck, FiDownload, FiEdit3, FiMessageCircle, FiPhone, FiSearch, FiSend, FiUser } from "react-icons/fi"
import { AppLayout } from "../../components/AppLayout"
import { Badge } from "../../components/Badge"

type Inquiry = {
  id: number
  name: string
  phone: string
  message: string
  created_at: string
  read: boolean
  status: "new" | "contacted" | "closed"
  notes: string
  whatsapp_url: string
  tel_url: string
}

type Props = {
  inquiries: Inquiry[]
  card_title: string | null
}

export default function CustomerInquiries({ inquiries, card_title }: Props) {
  const unread = inquiries.filter((inquiry) => !inquiry.read).length
  const [query, setQuery] = useState("")
  const [filter, setFilter] = useState<"all" | "unread" | "read">("all")
  const [notes, setNotes] = useState<Record<number, string>>(() => Object.fromEntries(inquiries.map((inquiry) => [inquiry.id, inquiry.notes])))
  const visibleInquiries = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase()
    return inquiries.filter((inquiry) => {
      const matchesFilter = filter === "all" || (filter === "unread" ? !inquiry.read : inquiry.read)
      const matchesQuery = !normalizedQuery || [inquiry.name, inquiry.phone, inquiry.message].some((value) => value.toLocaleLowerCase().includes(normalizedQuery))
      return matchesFilter && matchesQuery
    })
  }, [filter, inquiries, query])

  return (
    <AppLayout title="פניות" customerMode>
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <Link href="/dash" className="mb-3 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800"><FiArrowRight aria-hidden="true" />חזרה לכרטיס</Link>
          <h1 className="text-2xl font-bold sm:text-3xl">פניות</h1>
          <p className="mt-1 text-sm text-slate-600">הודעות שאנשים השאירו דרך {card_title || "הכרטיס שלך"}.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/dash/inquiries.csv" className="btn-secondary gap-2 px-3 py-2 text-sm"><FiDownload aria-hidden="true" />ייצוא</a>
          {unread > 0 ? <Badge tone="amber">{unread} חדשות</Badge> : null}
        </div>
      </div>

      {inquiries.length === 0 ? (
        <div className="glass-card mx-auto max-w-2xl p-8 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-brand-50 text-brand-700"><FiMessageCircle size={26} aria-hidden="true" /></div>
          <h2 className="mt-5 text-xl font-semibold">עדיין אין פניות</h2>
          <p className="mt-2 text-slate-600">כשמישהו ישאיר פרטים בכרטיס, ההודעה תופיע כאן.</p>
        </div>
      ) : (
        <>
          <div className="glass-card mb-4 space-y-3 p-4 sm:flex sm:items-center sm:gap-3 sm:space-y-0">
            <label className="relative block flex-1">
              <FiSearch className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="חיפוש לפי שם, טלפון או הודעה" className="w-full rounded-2xl border border-slate-200 bg-white py-3 pe-4 ps-10 text-sm outline-none transition focus:border-brand-400 focus:ring-2 focus:ring-brand-100" />
            </label>
            <div className="flex gap-2 overflow-x-auto" role="group" aria-label="סינון פניות">
              {(["all", "unread", "read"] as const).map((value) => {
                const labels = { all: "הכול", unread: "חדשות", read: "טופלו" }
                return <button key={value} type="button" onClick={() => setFilter(value)} className={`whitespace-nowrap rounded-xl px-3 py-2 text-sm font-medium ${filter === value ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{labels[value]}</button>
              })}
            </div>
          </div>
          {visibleInquiries.length === 0 ? <div className="glass-card p-8 text-center text-sm text-slate-500">לא נמצאו פניות מתאימות.</div> : null}
          <div className="space-y-3">
          {visibleInquiries.map((inquiry) => (
            <article key={inquiry.id} className={`glass-card p-5 ${inquiry.read ? "" : "border-brand-200 bg-brand-50/30"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-slate-100 text-slate-500"><FiUser aria-hidden="true" /></span>
                  <div className="min-w-0">
                    <h2 className="truncate font-semibold">{inquiry.name}</h2>
                    <a href={inquiry.tel_url} className="text-sm text-slate-500 hover:text-brand-700" dir="ltr">{inquiry.phone}</a>
                  </div>
                </div>
                {!inquiry.read ? <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-brand-500" title="פנייה חדשה" /> : null}
              </div>
              <p className="mt-4 whitespace-pre-wrap leading-7 text-slate-700">{inquiry.message}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {(["new", "contacted", "closed"] as const).map((status) => <button key={status} type="button" onClick={() => router.patch(`/dash/inquiries/${inquiry.id}`, { status }, { preserveScroll: true })} className={`rounded-xl px-3 py-1.5 text-xs font-medium ${inquiry.status === status ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{statusLabels[status]}</button>)}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
                <a href={`${inquiry.whatsapp_url}?text=${encodeURIComponent("שלום " + inquiry.name + ", תודה שפנית אלינו. אחזור אליך בהקדם.")}`} target="_blank" rel="noreferrer" className="btn-primary gap-2 bg-emerald-600 hover:bg-emerald-700"><FiMessageCircle aria-hidden="true" />וואטסאפ</a>
                <a href={`${inquiry.whatsapp_url}?text=${encodeURIComponent("שלום " + inquiry.name + ", אפשר לתאם שיחה? מתי נוח לך?")}`} target="_blank" rel="noreferrer" className="btn-secondary gap-2"><FiSend aria-hidden="true" />תיאום שיחה</a>
                <a href={inquiry.tel_url} className="btn-secondary gap-2"><FiPhone aria-hidden="true" />התקשרות</a>
                {!inquiry.read ? <button type="button" className="btn-secondary gap-2" onClick={() => router.patch(`/dash/inquiries/${inquiry.id}/read`)}><FiCheck aria-hidden="true" />סימון כטופל</button> : <span className="text-xs text-slate-400">טופל</span>}
                <time className="ms-auto text-xs text-slate-400" dateTime={inquiry.created_at}>{new Date(inquiry.created_at).toLocaleDateString("he-IL")}</time>
              </div>
              <div className="mt-4 rounded-2xl bg-slate-50 p-3">
                <div className="mb-2 flex items-center gap-2 text-xs font-medium text-slate-500"><FiEdit3 aria-hidden="true" />הערה פנימית</div>
                <textarea value={notes[inquiry.id] ?? ""} onChange={(event) => setNotes((current) => ({ ...current, [inquiry.id]: event.target.value }))} placeholder="למשל: לחזור ביום שני" rows={2} className="w-full resize-none rounded-xl border border-slate-200 bg-white p-3 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100" />
                <button type="button" className="mt-2 text-xs font-semibold text-brand-700" onClick={() => router.patch(`/dash/inquiries/${inquiry.id}`, { notes: notes[inquiry.id] ?? "" }, { preserveScroll: true })}>שמירת הערה</button>
              </div>
            </article>
          ))}
          </div>
        </>
      )}
    </AppLayout>
  )
}

const statusLabels = { new: "חדש", contacted: "נוצר קשר", closed: "טופל" }
